import { Link } from '@inertiajs/react';
import React, { useState } from 'react';

import { ValueCard } from '../components/ValueCard';

import nyaLogo from '../../assets/images/NYA.jpeg';

import { Eye, Target, Flame, Shield, Lightbulb, ArrowRight, Quote } from 'lucide-react';

const AboutPage: React.FC = () => {
  return (
    <div>
      {/* Page Header */}
      <section className='py-20 md:py-28 text-center bg-muted relative overflow-hidden'>
        <div className='absolute top-0 left-0 w-full h-full overflow-hidden z-0 opacity-30'>
          <div className='absolute -top-24 -left-24 w-96 h-96 rounded-full bg-primary/20 blur-3xl'></div>
          <div className='absolute top-1/2 right-0 w-64 h-64 rounded-full bg-secondary/20 blur-3xl'></div>
        </div>
        <div className='container mx-auto px-6 relative z-10'>
          <h1 className='text-4xl md:text-6xl font-bold text-foreground font-heading mb-6 opacity-0 animate-fade-in-up'>
            Together, we are creating a <span className='text-primary'>better Future</span>.
          </h1>
          <p
            className='text-xl text-muted-foreground max-w-3xl mx-auto opacity-0 animate-fade-in-up'
            style={{ animationDelay: '0.2s' }}
          >
            Support our mission with donations, volunteer your time, or partner with us to drive impactful change.
          </p>
        </div>
      </section>

      {/* Our Story / Intro */}
      <section className='py-20'>
        <div className='container mx-auto px-6'>
          <div className='grid lg:grid-cols-2 gap-16 items-center'>
            {/* Text Content */}
            <div
              className='space-y-8 order-2 lg:order-1 opacity-0 animate-slide-in-left'
              style={{ animationDelay: '0.3s' }}
            >
              <div>
                <h2 className='text-3xl md:text-4xl font-bold text-foreground mb-6 font-heading'>Our Origin Story</h2>
                <div className='prose prose-lg dark:prose-invert text-muted-foreground space-y-6 leading-relaxed'>
                  <p>
                    Digital Wave was brewed from the{' '}
                    <strong className='text-foreground'>NexStep Foundation project</strong> which mainly targeted
                    financial support for the less privileged students in the Kpone catchment area. Digital Wave was a
                    split-off concept dedicated to digital evangelism in Ghana with focus in Kpone and its neighboring
                    towns and communities. This subsequently branched out as a stand alone program in 2018.
                  </p>
                  <p>
                    This is a team effort and very well thought out program to focus on digital, science and technology
                    inclusion in Ghana and Africa in general. Kpone was a target area for the pilot program beginning in
                    December 2019. Many lessons learned from different iterations of the training intend to serve as
                    pivotal leverage for scalability across the greater Accra surrounding villages where this program is
                    needed the most.
                  </p>

                  <div className='bg-card border-l-4 border-primary p-6 rounded-r-lg shadow-sm mt-6 hover:shadow-md transition-shadow'>
                    <h3 className='text-lg font-bold text-foreground mb-2 font-heading'>Legal Status</h3>
                    <p className='text-sm text-muted-foreground'>
                      Digital Wave Africa Org is officially registered as a non-profit organization in the United
                      States, New Jersey as an active <strong>501(C)(3) charitable organization</strong> and has a good
                      standing with the Internal Revenue Service (IRS) and the Secretary of State of New Jersey.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Image Grid */}
            <div
              className='order-1 lg:order-2 grid gap-4 opacity-0 animate-slide-in-right'
              style={{ animationDelay: '0.4s' }}
            >
              <div className='relative rounded-2xl overflow-hidden shadow-xl border border-border transform hover:scale-[1.02] transition-transform duration-500'>
                <img
                  src='https://images.unsplash.com/photo-1531482615713-2afd69097998?q=80&w=2070&auto=format&fit=crop'
                  alt='Digital Wave Workshop'
                  className='w-full h-80 object-cover'
                />
                <div className='absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end p-6'>
                  <div className='text-white'>
                    <p className='font-bold text-lg'>Empowering Kpone-Katamanso</p>
                    <p className='text-sm opacity-90'>Since 2018</p>
                  </div>
                </div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <img
                  src='https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2070&auto=format&fit=crop'
                  alt='Teamwork'
                  className='rounded-xl shadow-md h-40 w-full object-cover border border-border hover:scale-105 transition-transform duration-500'
                />
                <img
                  src='https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=2070&auto=format&fit=crop'
                  alt='Mentorship'
                  className='rounded-xl shadow-md h-40 w-full object-cover border border-border hover:scale-105 transition-transform duration-500'
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className='py-20 bg-muted/50'>
        <div className='container mx-auto px-6'>
          <div className='grid md:grid-cols-2 gap-8'>
            <div
              className='bg-card p-10 rounded-2xl border border-border shadow-sm hover:shadow-md transition-all opacity-0 animate-fade-in-up'
              style={{ animationDelay: '0.1s' }}
            >
              <div className='w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-primary mb-6'>
                <Eye className='h-8 w-8' />
              </div>
              <h3 className='text-2xl font-bold text-foreground mb-4 font-heading'>Our Vision</h3>
              <p className='text-muted-foreground text-lg leading-relaxed'>
                To develop IT inclined individuals for community development and enhance standard of living to alleviate
                poverty.
              </p>
            </div>
            <div
              className='bg-card p-10 rounded-2xl border border-border shadow-sm hover:shadow-md transition-all opacity-0 animate-fade-in-up'
              style={{ animationDelay: '0.3s' }}
            >
              <div className='w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center text-secondary mb-6'>
                <Target className='h-8 w-8' />
              </div>
              <h3 className='text-2xl font-bold text-foreground mb-4 font-heading'>Our Mission</h3>
              <p className='text-muted-foreground text-lg leading-relaxed'>
                Equipping the youth in IT disciplines, by organizing regular workshops and giving them the platform to
                network for future opportunities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className='py-20'>
        <div className='container mx-auto px-6'>
          <div className='text-center max-w-3xl mx-auto mb-16'>
            <p className='text-sm font-bold tracking-widest text-secondary uppercase mb-3'>What Drives Us</p>
            <h2 className='text-3xl md:text-4xl font-bold text-foreground font-heading'>Our Core Values</h2>
            <p className='text-lg text-muted-foreground mt-4'>The guiding principles that drive our impact.</p>
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto'>
            <div className='opacity-0 animate-fade-in-up' style={{ animationDelay: '0.1s' }}>
              <ValueCard accent='primary' title='Passion' description='For one to be good at it, this person has to want it more.'>
                <Flame className='h-8 w-8' />
              </ValueCard>
            </div>
            <div className='opacity-0 animate-fade-in-up' style={{ animationDelay: '0.2s' }}>
              <ValueCard
                accent='secondary'
                title='Courage'
                description="Never be afraid to try, don't put your head down when you fail and never hesitate to start all over again."
              >
                <Shield className='h-8 w-8' />
              </ValueCard>
            </div>
            <div className='opacity-0 animate-fade-in-up' style={{ animationDelay: '0.3s' }}>
              <ValueCard
                accent='orange'
                title='Innovation'
                description="Thinking outside the box is a key ingredient to great discoveries. Hard work pays, don't get tired discovering."
              >
                <Lightbulb className='h-8 w-8' />
              </ValueCard>
            </div>
          </div>
        </div>
      </section>

      {/* Milestones Section */}
      <section className='py-20'>
        <div className='container mx-auto px-6'>
          <div className='max-w-4xl mx-auto'>
            <div className='text-center mb-12'>
              <h2 className='text-3xl font-bold text-foreground mb-4 font-heading'>Milestones & Successes</h2>
              <p className='text-muted-foreground text-lg'>
                We take pride in the achievements of our participants and the positive impact of our programs.
              </p>
            </div>

            <div
              className='bg-card p-8 md:p-12 rounded-2xl border border-border shadow-lg relative overflow-hidden hover:shadow-xl transition-shadow duration-300 opacity-0 animate-scale-in'
              style={{ animationDelay: '0.2s' }}
            >
              <div className='absolute top-0 right-0 p-4 opacity-5'>
                <Quote className='w-64 h-64' />
              </div>
              <div className='relative z-10 space-y-6'>
                <div className='flex items-start space-x-4'>
                  <div className='w-2 h-2 rounded-full bg-primary mt-2.5 flex-shrink-0'></div>
                  <p className='text-muted-foreground text-lg leading-relaxed'>
                    Our successes include <span className='font-bold text-foreground'>Mr. Nana Atuobi Yiadom</span>, a
                    regular participant and a renowned lecturer at the Ghana Institute of Management and Public
                    Administration (GIMPA), who professes how productive he's been at his job thanks to our training.
                  </p>
                </div>
                <div className='flex items-start space-x-4'>
                  <div className='w-2 h-2 rounded-full bg-secondary mt-2.5 flex-shrink-0'></div>
                  <p className='text-muted-foreground text-lg leading-relaxed'>
                    The program has given out a one-time tuition payment sponsorship to{' '}
                    <span className='font-bold text-foreground'>Mr. Bismark Mansro</span> to pursue an IT discipline at
                    the University of Ghana, Legon for being one of the outstanding participants in the 2024 edition,
                    along with awards to other outstanding participants.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Future Section */}
      <section className='py-20 bg-primary text-primary-foreground relative overflow-hidden'>
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className='container mx-auto px-6 text-center relative z-10'>
          <div className='max-w-3xl mx-auto'>
            <h2 className='text-3xl md:text-5xl font-bold font-heading mb-6 opacity-0 animate-fade-in-up'>
              Our Future
            </h2>
            <p
              className='text-xl text-primary-foreground/90 leading-relaxed mb-8 opacity-0 animate-fade-in-up'
              style={{ animationDelay: '0.2s' }}
            >
              Digital Wave is looking forward to impacting and influencing more lives in the coming years.
            </p>
            <p className='text-2xl font-bold opacity-0 animate-fade-in-up' style={{ animationDelay: '0.4s' }}>
              At Digital Wave, we say the future is IT!
            </p>
          </div>
        </div>
      </section>

      {/* Our Partners Section */}
      <section className='py-20 bg-background'>
        <div className='container mx-auto px-6'>
          <div className='text-center max-w-3xl mx-auto mb-16'>
            <h2 className='text-3xl md:text-4xl font-bold text-foreground font-heading'>Our Valued Partners</h2>
            <p className='text-lg text-muted-foreground mt-4'>Collaboration is at the heart of our success.</p>
          </div>
          <div
            className='max-w-5xl mx-auto bg-muted/30 rounded-2xl border border-border p-8 md:p-12 flex flex-col md:flex-row items-center gap-12 opacity-0 animate-fade-in'
            style={{ animationDelay: '0.3s' }}
          >
            <div className='w-full md:w-1/3 flex justify-center'>
              <div className='p-8 bg-white rounded-xl shadow-sm w-full flex items-center justify-center h-40 transform hover:scale-105 transition-transform duration-300'>
                <img
                  src={nyaLogo}
                  alt='National Youth Authority'
                  className='max-h-24 w-auto object-contain'
                />
              </div>
            </div>
            <div className='w-full md:w-2/3 text-center md:text-left'>
              <h3 className='text-2xl font-bold text-foreground mb-4 font-heading'>National Youth Authority</h3>
              <p className='text-muted-foreground leading-relaxed mb-6'>
                Our partnership enables us to reach more young people, providing them with critical digital skills.
                Together, we are building a stronger, tech-savvy generation for Ghana's future.
              </p>
              <Link
                href='/contact'
                className='font-semibold text-primary hover:text-primary/80 transition-colors inline-flex items-center group'
              >
                Partner With Us
                <ArrowRight className='w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform' />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;


// TODO: How we started and the lessons learned from those experiences
